import clsx from "clsx";
import { useEffect, useState } from "react";

import SearchInput from "common/components/SearchInput";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "common/components/ui/Tabs";
import { formatNumber, typedObjectEntries } from "common/helpers/utils";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { SelectedFacetParam } from "common/types";

import { DEFAULT_LIST_LEADS_SEARCH_PARAMS } from "../../../constants";
import useListLeadsTotals from "../../../datahooks/useListLeadsTotals";
import useListLeadsParams from "../../../hooks/useListLeadsParams";
import ExcludedLeads from "./ExcludedLeads";
import IncludedLeads from "./IncludedLeads";
import NoLeadsPlaceholder from "./NoLeadsPlaceholder";

import type { ListLeadsSearchParams } from "../../../types";

//totalKey picks the tab's own count out of the one totals call
const TABS = {
  leads: { label: "Leads", totalKey: "included" },
  excluded: { label: "Excluded", totalKey: "excluded" },
} as const;

const tabContentClassName = "flex w-full grow flex-col gap-y-4";

export default function LeadListLeads({ listId }: { listId: string }) {
  const isDesktop = useTwBreakpoint("lg");

  const { totals } = useListLeadsTotals({ listId });

  const [selectedTab, setSelectedTab] = useState<keyof typeof TABS>("leads");
  const [searchParams, setSearchParams] = useState<
    ListLeadsSearchParams<SelectedFacetParam>
  >(DEFAULT_LIST_LEADS_SEARCH_PARAMS);
  const { setSearchParam, resetSearchParam, resetParams } = useListLeadsParams(
    searchParams,
    setSearchParams,
  );

  useEffect(() => {
    resetParams();
  }, [selectedTab]);

  const leadsTabProps = {
    listId,
    searchParams,
    setSearchParams,
    searchComponent: (
      <SearchInput
        className="max-lg:flex-1 lg:max-w-72"
        placeholder={`Search ${TABS[selectedTab].label.toLowerCase()}`}
        variant={isDesktop ? "sm" : "lg"}
        value={searchParams.keyword}
        onChange={(e) => setSearchParam("keyword", e.currentTarget.value)}
        onClear={() => resetSearchParam("keyword")}
      />
    ),
  };

  return (
    <Tabs
      value={selectedTab}
      onValueChange={(value) => setSelectedTab(value as keyof typeof TABS)}
      className="flex w-full grow flex-col gap-y-4"
    >
      <TabsList size={isDesktop ? "sm" : "md"}>
        {typedObjectEntries(TABS).map(([value, { label, totalKey }]) => (
          <TabsTrigger key={value} value={value}>
            {totals ? (
              <>
                {label}
                {!!totals[totalKey] && (
                  <div
                    className={clsx(
                      "h-4 px-1 rounded-md text-button-xxs",
                      value === selectedTab
                        ? "bg-black-950 text-white"
                        : "bg-black-200 text-black-700",
                    )}
                  >
                    {formatNumber(totals[totalKey])}
                  </div>
                )}
              </>
            ) : (
              label
            )}
          </TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="leads" className={tabContentClassName}>
        <IncludedLeads {...leadsTabProps} />
      </TabsContent>

      <TabsContent value="excluded" className={tabContentClassName}>
        {totals && totals.excluded === 0 ? (
          <NoLeadsPlaceholder type="excluded" />
        ) : (
          <ExcludedLeads {...leadsTabProps} />
        )}
      </TabsContent>
    </Tabs>
  );
}
